/**
 * Pul boshqaruvi API. Kalitlar real-time hodisalari bilan mos: "money_account" (hisoblar, qoldiq), "transaction" (daftar);
 * moliya paneli -- ["budget", "finance", ...]: budjet obyektlari (xarajat, maosh, bonus, carrier to'lovi) va pul
 * harakatlari o'zgarganda useBudgetRealtime yangilaydi.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Paginated, type RequestOptions, useIdempotencyKeys } from "@/lib/api";
import type { Currency, Direction, FinanceOverview, MoneyAccount, Rate, Transaction } from "./types";

type Query = RequestOptions["query"];

export const financeKeys = {
  accounts: (archived = false) => ["money_account", "list", { archived }] as const,
  transactions: (q: Query) => ["transaction", "list", q] as const,
  overview: (q: Query) => ["budget", "finance", q] as const,
  rates: ["exchange_rate"] as const,
};

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    for (const key of ["money_account", "transaction", "budget", "expense", "salary", "bonus", "application"]) {
      void qc.invalidateQueries({ queryKey: [key] });
    }
  };
}

export function useMoneyAccounts(enabled = true, archived = false) {
  return useQuery({
    queryKey: financeKeys.accounts(archived),
    queryFn: () => api.get<MoneyAccount[]>("/api/finance/accounts/", { query: archived ? { archived: 1 } : undefined }),
    enabled,
    staleTime: 30_000,
  });
}

export type AccountWrite = Partial<Pick<MoneyAccount, "name" | "kind" | "currency" | "bank" | "card_last4" | "branch" | "opening_date" | "note">> & {
  opening_balance?: string;
  version?: number;
};

export function useSaveAccount() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...data }: AccountWrite & { id?: number }) =>
      id ? api.patch<MoneyAccount>(`/api/finance/accounts/${id}/`, data) : api.post<MoneyAccount>("/api/finance/accounts/", data),
    onSettled: invalidate,
  });
}

/** Arxivdagi hisobni qaytarish (owner): undan qilingan to'lovni bekor qilish uchun ham kerak -- arxivdagi hisob yozuvlari o'zgarmaydi */
export function useRestoreAccount() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => api.post<MoneyAccount>(`/api/finance/accounts/${id}/restore/`), onSettled: invalidate });
}

/** O'chirish = arxiv (backend): tasdiq oynasi MoneyPage'da, qoldiq bo'lsa ham */
export function useDeleteAccount() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => api.delete(`/api/finance/accounts/${id}/`), onSettled: invalidate });
}

export function useTransactions(query: Query) {
  return useQuery({
    queryKey: financeKeys.transactions(query),
    queryFn: () => api.get<Paginated<Transaction>>("/api/finance/transactions/", { query }),
    placeholderData: keepPreviousData,
  });
}

export interface MoveWrite {
  /** "HH:MM:SS" -- bo'sh bo'lsa bugungi yozuvda server hozirgi vaqtni qo'yadi */
  time?: string;
  account: number;
  kind: "income" | "expense" | "adjustment";
  direction?: Direction;
  amount: string;
  date: string;
  category?: string;
  branch?: number | null;
  description?: string;
  counterparty?: string;
  /** Komissiya foizi; "" -- komissiyasiz, berilmasa -- kategoriyaniki (server) */
  commission_pct?: string;
}

/** Tez chiqimda chek (fayl) bo'lishi mumkin: u bilan multipart, aks holda JSON. Idempotency kaliti faqat matn maydonlaridan. */
export type MoveInput = MoveWrite & { receipt?: File | null };

function moveFormData(data: MoveWrite, receipt: File): FormData {
  const fd = new FormData();
  for (const [k, v] of Object.entries(data)) if (v !== undefined && v !== null) fd.append(k, String(v));
  fd.append("receipt", receipt);
  return fd;
}

export function useCreateMove() {
  const invalidate = useInvalidate();
  const keys = useIdempotencyKeys();  // javob kelmay qayta yuborilsa ikki kirim yozilmaydi
  return useMutation({
    mutationFn: ({ receipt, ...data }: MoveInput) =>
      receipt
        ? api.post<Transaction>("/api/finance/transactions/", undefined, { formData: moveFormData(data, receipt), idempotencyKey: keys.for(data) })
        : api.post<Transaction>("/api/finance/transactions/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, { receipt: _r, ...data }) => keys.done(data),
    onSettled: invalidate,
  });
}

export function useUpdateMove() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; version: number; description?: string; counterparty?: string; category?: string; date?: string; time?: string; amount?: string; commission_pct?: string }) =>
      api.patch<Transaction>(`/api/finance/transactions/${id}/`, data),
    onSettled: invalidate,
  });
}

export function useVoidMove() {
  const invalidate = useInvalidate();
  return useMutation({ mutationFn: (id: number) => api.delete(`/api/finance/transactions/${id}/`), onSettled: invalidate });
}

export function useTransfer() {
  const invalidate = useInvalidate();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: (data: { from_account: number; to_account: number; amount: string; received?: string; date: string; time?: string; description?: string }) =>
      api.post("/api/finance/transfers/", data, { idempotencyKey: keys.for(data) }),
    onSuccess: (_result, data) => keys.done(data),
    onSettled: invalidate,
  });
}

/** Qoldiqni solishtirish (owner): haqiqiy qoldiq -> farq bitta tuzatish yozuvi (backend `accounts/<id>/reconcile/`) */
export function useReconcile() {
  const invalidate = useInvalidate();
  const keys = useIdempotencyKeys();
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; actual_balance: string; date: string; time?: string; note?: string }) =>
      api.post<{ transaction: Transaction; balance: string }>(`/api/finance/accounts/${id}/reconcile/`, data, { idempotencyKey: keys.for({ id, ...data }) }),
    onSuccess: (_result, data) => keys.done(data),
    onSettled: invalidate,
  });
}

export function useFinanceOverview(query: Query, enabled = true) {
  return useQuery({
    queryKey: financeKeys.overview(query),
    queryFn: () => api.get<FinanceOverview>("/api/finance/overview/", { query }),
    enabled,
    placeholderData: keepPreviousData,
  });
}

export function useRates(enabled = true) {
  return useQuery({ queryKey: financeKeys.rates, queryFn: () => api.get<Rate[]>("/api/finance/rates/"), enabled, staleTime: 10 * 60_000 });
}

export function useSetRate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { usd_uzs: string; date?: string }) => api.post<Rate>("/api/finance/rates/", data),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: financeKeys.rates });
      void qc.invalidateQueries({ queryKey: ["budget"] });
    },
  });
}

/** Joriy kurs (1 USD = N UZS): to'lov oynalarida taxminiy summani ko'rsatish uchun */
export function useCurrentRate(enabled = true): number | null {
  const rates = useRates(enabled);
  const r = rates.data?.[0];
  return r ? Number(r.usd_uzs) : null;
}

export type { Currency };
